import { isFuture } from 'date-fns'

import { getCauses, selectOperatingDateForTask } from '../Selectors'
import { Delay, DelayCause, Task, TimetableRow, TrainPunctuality } from '../types/Input'

export type TimeTableType = 'ARRIVAL' | 'DEPARTURE'

export const getTimeTableRowByStation = (
  rows: TimetableRow[],
  type: TimeTableType,
  station: string
): TimetableRow | undefined => {
  return rows.find((t) => t.type === type && t.stationShortCode === station)
}

export const isEstimate = (t: TimetableRow): boolean => {
  return !!t.liveEstimateTime && !t.actualTime && isFuture(new Date(t.liveEstimateTime))
}

/** Collects all timetable rows that fall within the fromStation → toStation leg. */
const collectLegRows = (
  from: string,
  to: string,
  punctuality: TrainPunctuality
): TimetableRow[] => {
  const rows: TimetableRow[] = []
  let between = false
  for (const t of punctuality.timeTableRows) {
    if (!between && t.stationShortCode === from) {
      between = true
    }

    if (between) {
      rows.push(t)
    }

    if (between && t.stationShortCode === to) {
      break
    }
  }
  return rows
}

export const getDelayMinutes = (
  from: string,
  to: string,
  punctuality: TrainPunctuality
): number => {
  return getCurrentRow(from, to, punctuality)?.differenceInMinutes ?? 0
}

const getRowCause = (t: TimetableRow): DelayCause[] => {
  return [t.arrivalScheduleDeviationReason, t.departureScheduleDeviationReason].filter(Boolean)
}

const getCauseForRowType = (row: TimetableRow): DelayCause | null | undefined => {
  switch (row.type) {
    case 'ARRIVAL':
      return row.arrivalScheduleDeviationReason
    case 'DEPARTURE':
      return row.departureScheduleDeviationReason
    default:
      return undefined
  }
}

const isEquipmentFaultCause = (cause: DelayCause): boolean =>
  cause.categoryCode.startsWith('K') || cause.categoryCode.startsWith('V')

const hasEquipmentFaultCauseForRowType = (row: TimetableRow): boolean => {
  const cause = getCauseForRowType(row)
  return cause ? isEquipmentFaultCause(cause) : false
}

export const getQualifyingEquipmentFaultDelayRows = (
  from: string,
  to: string,
  punctuality: TrainPunctuality,
  minimumDelayMinutes: number
): TimetableRow[] =>
  collectLegRows(from, to, punctuality).filter(
    (row) => row.differenceInMinutes >= minimumDelayMinutes && hasEquipmentFaultCauseForRowType(row)
  )

export const collectTaskPunctuality = (
  from: string,
  to: string,
  punctuality: TrainPunctuality
): Array<Delay> => {
  return collectLegRows(from, to, punctuality).map((t) => {
    return {
      causes: getRowCause(t),
      differenceInMinutes: t.differenceInMinutes,
    }
  })
}

/**
 * Returns true when any of the delays carries an equipment-fault-related cause
 * code (Digitraffic cause category K or V) — the delays that the driver must
 * handle via the delay notification flow.
 */
export const isEquipmentFaultDelay = (delays: Array<Delay>): boolean => {
  const causes = getCauses(delays)
  return causes.some(isEquipmentFaultCause)
}

/**
 * Builds the persistence key for a task's delay notification.
 *
 * The key must be stable across sessions and devices because handled
 * notifications are stored server-side against it. Task ids are just array
 * indexes assigned client-side (see lib/data.ts) and shift ids can differ
 * between the own-shift list and searched shifts, so the key is derived from
 * the train run itself: train number + operating date + leg. The same delay
 * therefore shares one key no matter which view or shift instance renders it.
 *
 * Note: the CE endpoint validates keys against ^[a-zA-Z0-9_\-,]*$, so only
 * dash-separated alphanumerics may be used here.
 */
export const getTaskDelayNotificationKey = (task: Task): string =>
  `${task.trainNumberNumeric}-${selectOperatingDateForTask(task)}-${task.fromStation}-${
    task.toStation
  }`

export const getCurrentRow = (
  from: string,
  to: string,
  punctuality: TrainPunctuality
): TimetableRow | null => {
  const legRows = collectLegRows(from, to, punctuality)

  const start = getTimeTableRowByStation(legRows, 'DEPARTURE', from)
  const end = getTimeTableRowByStation(legRows, 'ARRIVAL', to)

  if (start && isEstimate(start)) return start
  if (end && !isEstimate(end)) return end

  const nonEstimates = punctuality.timeTableRows.filter((t) => !isEstimate(t))
  return nonEstimates.length > 0 ? nonEstimates[nonEstimates.length - 1] : null
}
